// **REACT IMPORTS
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

// **THIRD PARTY IMPORTS
import debounce from 'lodash.debounce'
import { Device } from 'mediasoup-client'
import { useLocation, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { io } from 'socket.io-client'
import { v4 } from 'uuid'

// **ENV IMPORTS
import { MEDIA_SERVER_URL_MAP } from '../env/env'

// **Utils Imports
import {
  audioStreamCodes,
  ICE_SERVERS,
  interviewVideoStreamCodecs,
  needFullQualityVideoPath,
  ROLES,
  videoStreamCodecs
} from '../utils/constant'
import {
  detectClientBrowserName,
  formatProducerObj,
  getAudioDeviceId,
  getAudioOutputDeviceIdFilter,
  getUserDetails,
  setAudioDeviceId,
  setGetAudioVideoLoading,
  swalAlert
} from '../utils/utils'

// ** Component Imports
import ToastContent from '../components/chat-item/ToastContent'
import ErrorBoundary from '../pages/error'

// **Notification Sound Import
import NotificationSound from '../assets/audio/notification.mp3'

// **Redux Imports
import { useDispatch, useSelector } from 'react-redux'

// **API Imports
import { candidateAttemptEvents } from '../axios/api/candidate'

// **Context Imports
import { setAudioOutputDeviceId } from '../store/slice/editor'
import { SocketContext } from './socket'

export const MeetingContext = createContext(null)

const MeetingProvider = ({ children }) => {
  // Hooks
  const dispatch = useDispatch()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  // Redux hooks
  const userProfileDetails = useSelector((state) => state.auth.profile)

  //Context Imports
  const {
    isSocketConnected,
    socketAuthenticated,
    socket: editorSocket,
    getMediaServerEndPoint
  } = useContext(SocketContext)

  // Refs
  const device = useRef(null)
  const socket = useRef(null)
  const producerIds = useRef([])
  const consumerQueue = useRef([])
  const videoProducer = useRef(null)
  const needVideoRef = useRef(false)
  const devicesStateRef = useRef({})
  const audioProducer = useRef(null)
  const localStreamRef = useRef(null)
  const reversedUrlRef = useRef(null)
  const chatRoomNameRef = useRef(null)
  const consumingTransport = useRef({})
  const excludeEndpointsRef = useRef([])
  const consumerTransport = useRef(null)
  const currentlySupervising = useRef([])
  const producersTransport = useRef(null)
  const routerCapabilities = useRef(null)
  const screenShareVideoProducer = useRef(null)
  const screenShareAudioProducer = useRef(null)
  const screenShareAudioContext = useRef(null)
  const screenShareAudioSource = useRef(null)
  const previousMetadataRef = useRef(null)
  const webcamMediaRecordRef = useRef(null)
  const screenMediaRecorderRef = useRef(null)
  const localScreenShareStreamRef = useRef(null)
  const screenShareMutedTimeoutRef = useRef(null)
  const screenShareImageCaptureRef = useRef(null)
  const selectedAudioInputDeviceIdRef = useRef(null)
  const selectedAudioInputGroupIdRef = useRef(null)
  const screenShareClonedStreamRef = useRef(null)
  const meetingInitInFlightRef = useRef(false)
  const meetingGenRef = useRef(0)
  const meetingClosingRef = useRef(false)
  const currentStageAttempIds = useRef({ attemptId: null, stageId: null })

  // State
  const [chats, setChats] = useState({})
  const [peerInfo, setPeerInfo] = useState({})
  const [mediaState, setMediaState] = useState({
    audio: false,
    webcam: false,
    screen: false,
    handRaised: false
  })
  const [newStream, setNewStream] = useState({})
  const [updatedChats, setUpdatedChats] = useState({})
  const [localStream, setLocalStream] = useState(null)
  const [supervisedUser, setSupervisedUser] = useState([])
  const [userRoomJoined, setUserRoomJoined] = useState(false)
  const [audioInputDevices, setAudioInputDevices] = useState([])
  const [connectedUserList, setConnectedUserList] = useState([])
  const [endInterviewRoom, setEndInterviewRoom] = useState(false)
  const [audioOutputDevices, setAudioOutputDevices] = useState([])
  const [isOnline, setIsOnline] = useState(window.navigator.onLine)
  const [isMediaServerConnected, setIsMediaServerConnected] = useState(false)
  const [selectedAudioInputDeviceId, setSelectedAudioInputDeviceId] = useState({
    deviceId: null,
    groupId: null
  })

  const [localScreenShareStream, setLocalScreenShareStream] = useState(null)
  const [producersTransportState, setProducersTransportState] = useState(null)
  const [recallGetEndPoint, setRecallGetEndpoint] = useState({
    call: false,
    stageId: null,
    attemptId: null,
    excludeEndpoints: []
  })

  // New States

  // Variable
  const attemptId = useMemo(() => {
    return searchParams.get('attemptId') || null
  }, [searchParams])

  // Functions

  const closeMeetingSocketFully = useCallback(async () => {
    const s = socket.current
    if (!s) return

    meetingClosingRef.current = true

    try {
      s.removeAllListeners?.()
    } catch {}
    try {
      s.off?.()
    } catch {}

    await new Promise((resolve) => {
      let done = false
      const finish = () => {
        if (done) return
        done = true
        resolve()
      }

      try {
        s.once?.('disconnect', finish)
      } catch {}

      try {
        s.disconnect?.()
      } catch {}

      setTimeout(finish, 1000)
    })

    socket.current = null
    meetingClosingRef.current = false
  }, [])

  const handleEndInterviewForAll = useCallback((roomName) => {
    socket?.current?.emit('end-interview-room', { roomName })
  }, [])

  const handleChatRoomName = useCallback((roomName) => {
    chatRoomNameRef.current = roomName
  }, [])

  const handleUpdatePeerInfoProperties = useCallback((key, value) => {
    setPeerInfo((prev) => ({
      ...prev,
      properties: { ...(prev?.properties || {}), [key]: value }
    }))
  }, [])

  const handleProducerPaused = useCallback((paused, type) => {
    setMediaState((prev) => ({ ...prev, [type]: paused }))
  }, [])

  const endMediaProducer = useCallback((mediaType) => {
    const producerTypeToClose =
      mediaType === 'screen' ? ['screen'] : ['webcam', 'audio']

    producerTypeToClose?.forEach((type) => {
      socket?.current?.emit('producer-close', { type })
    })

    if (mediaType === 'screen') {
      if (screenMediaRecorderRef.current) {
        screenMediaRecorderRef.current?.stop()
        screenMediaRecorderRef.current = null
      }

      //Closing Producer
      screenShareVideoProducer?.current?.close()
      screenShareAudioProducer?.current?.close()
      screenShareAudioContext?.current?.close()
      screenShareAudioSource?.current?.stop()

      screenShareVideoProducer.current = null
      screenShareAudioProducer.current = null
      screenShareAudioContext.current = null
      screenShareAudioSource.current = null

      // Clear ImageCapture reference when stopping screen share
      screenShareImageCaptureRef.current = null

      localScreenShareStreamRef?.current
        ?.getVideoTracks()?.[0]
        ?.removeEventListener('mute', screenShareMuted)
      localScreenShareStreamRef?.current
        ?.getVideoTracks()?.[0]
        ?.removeEventListener('unmute', screenShareUnmuted)
      localScreenShareStreamRef?.current
        ?.getVideoTracks()?.[0]
        ?.removeEventListener('ended', screenShareEnded)

      localScreenShareStreamRef?.current
        ?.getTracks()
        ?.forEach((track) => track.stop())

      localScreenShareStreamRef.current = null

      screenShareClonedStreamRef?.current
        ?.getTracks()
        ?.forEach((track) => track.stop())

      screenShareClonedStreamRef.current = null

      setLocalScreenShareStream(null)
    } else {
      if (webcamMediaRecordRef.current) {
        webcamMediaRecordRef.current?.stop()
        webcamMediaRecordRef.current = null
      }

      //Closing Producers
      audioProducer?.current?.close()
      videoProducer?.current?.close()

      audioProducer.current = null
      videoProducer.current = null

      localStreamRef?.current
        ?.getAudioTracks()?.[0]
        ?.removeEventListener('mute', webcamAudioMuted)
      localStreamRef?.current
        ?.getAudioTracks()?.[0]
        ?.removeEventListener('unmute', webcamAudioUnmuted)
      localStreamRef?.current
        ?.getAudioTracks()?.[0]
        ?.removeEventListener('ended', webcamAudioEnded)

      localStreamRef?.current
        ?.getVideoTracks()?.[0]
        ?.removeEventListener('ended', webcamVideoEnded)

      localStreamRef?.current?.getTracks()?.forEach((track) => track.stop())
      localStreamRef.current = null
      setLocalStream(null)
    }

    setPeerInfo((prev) => ({
      ...prev,
      ...(mediaType === 'screen'
        ? { screenStreamId: null }
        : { webcamAudioStreamId: null, webcamVideoStreamId: null }),
      properties: {
        ...(prev?.properties || {}),
        producers:
          prev?.properties?.producers?.filter(
            (producer) => !producerTypeToClose.includes(producer.kind)
          ) || []
      }
    }))

    producerIds.current = producerIds.current.filter(
      (producer) => !producerTypeToClose.includes(producer.kind)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const webcamAudioMuted = useCallback(() => {
    handleProducerPaused(true, 'audio')
  }, [handleProducerPaused])

  const webcamAudioUnmuted = useCallback(() => {
    handleProducerPaused(false, 'audio')
  }, [handleProducerPaused])

  const webcamAudioEnded = useCallback(() => {
    handleProducerPaused(true, 'audio')
    endMediaProducer('audio')
  }, [endMediaProducer, handleProducerPaused])

  const webcamVideoEnded = useCallback(() => {
    endMediaProducer('webcam')
  }, [endMediaProducer])

  const screenShareMuted = useCallback(() => {
    if (navigator?.onLine) {
      screenShareMutedTimeoutRef.current = setTimeout(() => {
        const track = localScreenShareStreamRef.current?.getVideoTracks?.()[0]
        if (track && track.muted) {
          endMediaProducer('screen')
        }
      }, 8000)
    }
  }, [endMediaProducer])

  const screenShareUnmuted = useCallback(() => {
    if (screenShareMutedTimeoutRef.current) {
      clearTimeout(screenShareMutedTimeoutRef.current)
      screenShareMutedTimeoutRef.current = null
    }
  }, [])

  const screenShareEnded = useCallback(() => {
    if (screenShareMutedTimeoutRef.current) {
      clearTimeout(screenShareMutedTimeoutRef.current)
      screenShareMutedTimeoutRef.current = null
    }
    // Clear ImageCapture reference when screen share ends
    screenShareImageCaptureRef.current = null
    endMediaProducer('screen')
  }, [endMediaProducer])

  const getEnumeratedDevices = useCallback(() => {
    return new Promise((resolve, reject) => {
      navigator?.mediaDevices
        ?.getUserMedia({ audio: true, video: false })
        ?.then((stream) => {
          const systemDeviceId =
            stream?.getAudioTracks()?.[0]?.getSettings()?.deviceId || null
          navigator?.mediaDevices?.enumerateDevices().then((devices) => {
            const inputs = devices?.filter(
              (device) =>
                device?.kind === 'audioinput' &&
                device?.deviceId !== 'communications'
            )

            const outputs = devices?.filter(
              (device) =>
                device?.kind === 'audiooutput' &&
                device?.deviceId !== 'communications' &&
                device?.deviceId !== 'default' &&
                !device?.label?.toLowerCase()?.includes('display') &&
                !device?.label?.toLowerCase()?.includes('(virtual)')
            )

            setAudioInputDevices(inputs)
            setAudioOutputDevices(outputs)

            resolve({
              inputs,
              outputs,
              systemDeviceId
            })
          })
          stream.getTracks().forEach(function (track) {
            track.stop()
          })
        })
        .catch((err) => {
          reject(err)
        })
    })
  }, [])

  const onRefreshStreamClick = useCallback((userObj) => {
    socket?.current?.emit('refresh-candidate', {
      hostname: userObj?.hostname,
      worker: userObj?.worker,
      roomName: userObj?.roomName,
      socketId: userObj?.socketId
    })
  }, [])

  const handleMuteUser = useCallback((userObj) => {
    socket?.current?.emit('mute-user', {
      hostname: userObj?.hostname,
      worker: userObj?.worker,
      roomName: userObj?.roomName,
      socketId: userObj?.socketId
    })
  }, [])

  const handleCandidateWarning = useCallback((userObj, text = '') => {
    socket.current?.emit('warn-candidate', {
      hostname: userObj?.hostname,
      worker: userObj?.worker,
      roomName: userObj?.roomName,
      socketId: userObj?.socketId,
      text
    })
  }, [])

  const playNotificationSound = useCallback(() => {
    const audio = new Audio(NotificationSound)
    audio.play().catch((error) => {})
  }, [])

  const showNotification = useCallback((chat) => {
    const description =
      chat?.sendTo === null
        ? `Message in group chat by ${chat?.sendBy?.name}`
        : `Message from ${chat?.sendBy?.name || 'supervisor'}`

    toast(
      ({ closeToast }) => (
        <ToastContent
          closeToast={closeToast}
          description={description}
          retryAction={() => {
            if (window.eventEmitter) {
              window.eventEmitter.emit('sender-email', {
                email: chat?.sendTo === null ? '@all' : chat?.sendBy?.email
              })
            }
          }}
        />
      ),
      {
        autoClose: 5000,
        closeOnClick: false,
        hideProgressBar: true,
        pauseOnHover: true,
        draggable: false
      }
    )
  }, [])

  const groupReceiveMessage = useCallback((chats) => {
    const obj = {}
    const details = getUserDetails()
    chats.forEach((chat) => {
      if (
        !chatRoomNameRef.current ||
        chatRoomNameRef.current === chat?.properties?.roomName
      ) {
        if (
          details?.roles?.toLowerCase() !== ROLES.CANDIDATE ||
          chat?.sendBy?.roles?.toLowerCase() !== ROLES.CANDIDATE ||
          chat?.sendBy?.email === details?.sub
        ) {
          if (!chat.sendTo) {
            obj['@all'] = [...(obj?.['@all'] || []), chat]
          } else if (chat?.sendTo && chat?.sendTo?.email === details?.sub) {
            obj[chat?.sendBy?.email] = [
              ...(obj?.[chat?.sendBy?.email] || []),
              chat
            ]
          } else if (chat?.sendTo && chat?.sendBy?.email === details?.sub) {
            obj[chat?.sendTo?.email] = [
              ...(obj?.[chat?.sendTo?.email] || []),
              chat
            ]
          }
        }
      }
    })
    setChats(obj)
    setUpdatedChats(obj)
  }, [])

  const receiveChatMessage = useCallback(
    (chat) => {
      if (
        !chatRoomNameRef.current ||
        chatRoomNameRef.current === chat?.properties?.roomName
      ) {
        const details = getUserDetails()
        function playSendNotification() {
          if (
            chat.sendBy?.email !== details?.sub ||
            chat?.sendBy?.roles !== details?.roles
          ) {
            playNotificationSound()
            showNotification(chat)
          }
        }

        setUpdatedChats((prev) => {
          const obj = { ...prev }
          if (
            details?.roles?.toLowerCase() !== ROLES.CANDIDATE ||
            chat?.sendBy?.roles?.toLowerCase() !== ROLES.CANDIDATE ||
            chat?.sendBy?.email === details?.sub
          ) {
            if (!chat.sendTo) {
              obj['@all'] = [...(obj?.['@all'] || []), chat]
              playSendNotification()
            } else if (chat?.sendTo && chat?.sendTo?.email === details?.sub) {
              obj[chat?.sendBy?.email] = [
                ...(obj?.[chat?.sendBy?.email] || []),
                chat
              ]
              playSendNotification()
            } else if (chat?.sendTo && chat?.sendBy?.email === details?.sub) {
              obj[chat?.sendTo?.email] = [
                ...(obj?.[chat?.sendTo?.email] || []),
                chat
              ]
              playSendNotification()
            }
          }

          return obj
        })
      }
    },
    [playNotificationSound, showNotification]
  )

  const handleMeetingEditorSocketMessages = useCallback(
    (event) => {
      const data = JSON.parse(event?.data || null)
      if (data?.method === 'media-server/worker-metadata') {
        const strifyData = JSON.stringify(data?.parameters)
        if (previousMetadataRef.current !== strifyData) {
          previousMetadataRef.current = strifyData
          const formattedProducer = formatProducerObj(data?.parameters)
          setConnectedUserList(formattedProducer || [])
        }
      } else if (data?.method === 'chat-room/receive-message') {
        receiveChatMessage(data?.parameters)
      } else if (data?.method === 'chat-room/get-all-messages-response') {
        groupReceiveMessage(data?.parameters?.messages || [])
      }
    },
    [receiveChatMessage, groupReceiveMessage]
  )

  const sendMessage = useCallback(
    (method, parameters = {}) => {
      if (editorSocket && editorSocket.readyState === WebSocket.OPEN) {
        editorSocket.send(JSON.stringify({ id: v4(), method, parameters }))
      }
    },
    [editorSocket]
  )

  const sendCandidateEvent = useCallback(
    (event, eventDetails) => {
      if (attemptId) {
        const payload = {
          event,
          attemptId,
          eventDetails
        }
        candidateAttemptEvents(payload)
      }
    },
    [attemptId]
  )

  const getAllChatMessages = useCallback(() => {
    sendMessage('chat-room/get-all-messages', {})
  }, [sendMessage])

  const handleUpdateChats = useCallback((key, value) => {
    if (value?.length > 0) {
      setChats((prev) => {
        return { ...prev, [key]: value }
      })
    }
  }, [])

  const sendChatMessage = useCallback(
    ({ chat }) => {
      sendMessage('chat-room/send-message', chat)
    },
    [sendMessage]
  )

  const handleSuperviseCandidateSocket = useCallback(
    (candidateSocketId, isSupervised) => {
      socket?.current?.emit('supervise-candidate', {
        candidateSocketId,
        isSupervised
      })

      if (isSupervised) {
        currentlySupervising.current = [
          ...currentlySupervising.current,
          candidateSocketId
        ]
      } else {
        currentlySupervising.current = currentlySupervising.current.filter(
          (id) => id !== candidateSocketId
        )
      }
    },
    []
  )

  const connectRecvTransport = useCallback(
    async (
      remoteProducerId,
      serverConsumerTransportId,
      hostname,
      workerId,
      roomName
    ) => {
      await socket?.current?.emit('consume', {
        rtpCapabilities: device.current.rtpCapabilities,
        remoteProducerId,
        serverConsumerTransportId,
        hostname,
        workerId,
        roomName
      })
    },
    []
  )

  const closeConsumer = useCallback((remoteProducerId) => {
    const producerToClose = consumingTransport?.current?.[remoteProducerId]

    if (producerToClose) {
      producerToClose?.consumer?.close()

      delete consumingTransport?.current?.[remoteProducerId]

      setPeerInfo((prev) => ({
        ...prev,
        properties: {
          ...(prev?.properties || {}),
          consumers:
            prev?.properties?.consumers?.filter(
              (c) => c?.producerId !== remoteProducerId
            ) || []
        }
      }))

      setNewStream((prev) => {
        const newData = { ...prev }
        delete newData?.[remoteProducerId]
        return newData
      })
    }
  }, [])

  const signalNewConsumerTransport = useCallback(
    async (remoteProducerId, candidateObj, type) => {
      const filterConsumingTransport =
        consumingTransport?.current?.[remoteProducerId]

      if (filterConsumingTransport) return

      consumingTransport.current[remoteProducerId] = {
        type,
        ...candidateObj,
        producerId: remoteProducerId
      }

      setPeerInfo((prev) => ({
        ...prev,
        properties: {
          ...(prev?.properties || {}),
          consumers: [
            ...(prev?.properties?.consumers || []),
            {
              producerId: remoteProducerId,
              socketId: candidateObj?.socketId,
              email: candidateObj?.email
            }
          ]
        }
      }))

      if (!consumerTransport?.current?.consumerTransport) {
        consumerQueue.current = [
          ...(consumerQueue.current || []),
          remoteProducerId
        ]
      } else {
        connectRecvTransport(
          remoteProducerId,
          consumerTransport.current.params.id,
          candidateObj?.hostname,
          candidateObj?.worker,
          candidateObj?.roomName
        )
      }
    },
    [connectRecvTransport]
  )

  const intialiseRecordingData = useCallback((type, id) => {
    const isWebmSupported = MediaRecorder.isTypeSupported(
      'video/webm;codecs=vp8'
    )

    socket?.current?.emit('stream-details', {
      id,
      type,
      fileName: `${type}.${isWebmSupported ? 'webm' : 'mp4'}`,
      timestamp: new Date().toISOString(),
      ContentType: `${isWebmSupported ? 'video/webm' : 'video/mp4'}`
    })
  }, [])

  const startSendingScreenShareData = useCallback((stream, id) => {
    screenMediaRecorderRef.current = new MediaRecorder(stream, {
      mimeType: MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
        ? 'video/webm;codecs=vp8'
        : 'video/mp4',
      videoBitsPerSecond: 250000
    })

    screenMediaRecorderRef.current.onerror = (event) => {
      console.error('MediaRecorder error:', event)
    }

    screenMediaRecorderRef.current.ondataavailable = async (event) => {
      if (event.data.size > 0) {
        const arrayBuffer = await event.data.arrayBuffer()
        setTimeout(() => {
          socket?.current?.emit('screen-share-data', { id, data: arrayBuffer })
        }, 0)
      }
    }

    screenMediaRecorderRef.current.onstop = () => {
      socket?.current?.emit('end-screen-share-stream', { id })
      screenMediaRecorderRef.current = null
    }

    try {
      screenMediaRecorderRef.current.start(3000) // Collect data every 4 seconds
    } catch (error) {
      console.error('Error starting MediaRecorder:', error)
    }
  }, [])

  const startSendingWebcamData = useCallback((stream, id) => {
    webcamMediaRecordRef.current = new MediaRecorder(stream, {
      mimeType: MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
        ? 'video/webm;codecs=vp8,opus'
        : 'video/mp4;codecs=avc1',
      videoBitsPerSecond: 250000,
      audioBitsPerSecond: 32000
    })

    webcamMediaRecordRef.current.onerror = (event) => {
      console.error('MediaRecorder error:', event)
    }

    webcamMediaRecordRef.current.ondataavailable = async (event) => {
      if (event.data.size > 0) {
        const arrayBuffer = await event.data.arrayBuffer()
        setTimeout(() => {
          socket?.current?.emit('webcam-data', { id, data: arrayBuffer })
        }, 0)
      }
    }

    webcamMediaRecordRef.current.onstop = () => {
      socket?.current?.emit('end-webcam-stream', { id })
      webcamMediaRecordRef.current = null
    }

    try {
      webcamMediaRecordRef.current.start(3000) // Collect data every 4 seconds
    } catch (error) {
      console.error('Error starting MediaRecorder:', error)
    }
  }, [])

  const handleAudioVideoProducer = useCallback(async () => {
    const id = v4()
    const details = getUserDetails()
    const isInterviewOrPlacementStage =
      window?.location?.pathname?.includes('/interview-stage/') ||
      window?.location?.pathname?.includes('/ppt-stage/')

    const isAIScreeningRound = window?.location?.pathname?.includes(
      '/ai-screening-stage/'
    )

    const clientBrowser = detectClientBrowserName()?.split(' ')?.[0] || ''

    if (producersTransport.current && localStreamRef?.current) {
      audioProducer.current = await producersTransport.current.produce({
        appData: isInterviewOrPlacementStage
          ? { source: 'audio' }
          : {
              source: 'audio',
              id,
              clientBrowser,
              isAIRound: isAIScreeningRound
            },
        track: localStreamRef.current.getAudioTracks()[0],
        stopTracks: false,
        ...audioStreamCodes
      })

      audioProducer.current.on('trackended', () => {
        endMediaProducer('audio')
      })

      audioProducer.current.on('transportclose', () => {})
    }

    if (
      localStreamRef?.current?.getVideoTracks()?.length > 0 &&
      producersTransport.current
    ) {
      videoProducer.current = await producersTransport.current.produce({
        appData: isInterviewOrPlacementStage
          ? { source: 'webcam' }
          : { source: 'webcam', id, clientBrowser },
        track: localStreamRef.current.getVideoTracks()[0],
        stopTracks: false,
        ...(chatRoomNameRef?.current
          ? { interviewVideoStreamCodecs }
          : { videoStreamCodecs })
      })

      videoProducer.current.on('trackended', () => {
        endMediaProducer('webcam')
      })

      videoProducer.current.on('transportclose', () => {})

      if (
        details?.roles?.toLowerCase() === ROLES.CANDIDATE &&
        !isInterviewOrPlacementStage
      ) {
        const id = v4()
        intialiseRecordingData('webcam', id)
        sendCandidateEvent('webcamStarted', {
          timestamp: new Date().toISOString()
        })
        startSendingWebcamData(localStreamRef.current, id)
      }
    }
  }, [
    endMediaProducer,
    sendCandidateEvent,
    intialiseRecordingData,
    startSendingWebcamData
  ])

  const handleScreenShareProducer = useCallback(async () => {
    const id = v4()
    const details = getUserDetails()
    const isInterviewOrPlacementStage =
      window?.location?.pathname?.includes('/interview-stage/') ||
      window?.location?.pathname?.includes('/ppt-stage/')
    const clientBrowser = detectClientBrowserName()?.split(' ')?.[0] || ''

    if (
      producersTransport.current &&
      localScreenShareStreamRef?.current?.getAudioTracks()?.length > 0
    ) {
      screenShareAudioProducer.current =
        await producersTransport.current.produce({
          appData: isInterviewOrPlacementStage
            ? { source: 'screenShareAudio' }
            : { source: 'screenShareAudio', id, clientBrowser },
          track: localScreenShareStreamRef.current.getAudioTracks()[0],
          stopTracks: false,
          ...audioStreamCodes
        })

      screenShareAudioProducer.current.on('trackended', () => {})

      screenShareAudioProducer.current.on('transportclose', () => {})
    }

    if (
      localScreenShareStreamRef?.current?.getVideoTracks()?.length > 0 &&
      producersTransport.current
    ) {
      screenShareVideoProducer.current =
        await producersTransport.current.produce({
          appData: isInterviewOrPlacementStage
            ? { source: 'screen' }
            : { source: 'screen', id, clientBrowser },
          track: localScreenShareStreamRef.current.getVideoTracks()[0],
          stopTracks: false,
          ...(chatRoomNameRef?.current
            ? { interviewVideoStreamCodecs }
            : { videoStreamCodecs })
        })

      screenShareVideoProducer.current.on('trackended', () => {
        endMediaProducer('screen')
      })

      screenShareVideoProducer.current.on('transportclose', () => {})

      if (
        details?.roles?.toLowerCase() === ROLES.CANDIDATE &&
        !isInterviewOrPlacementStage
      ) {
        const id = v4()
        intialiseRecordingData('screen', id)
        sendCandidateEvent('screenShareStarted', {
          timestamp: new Date().toISOString()
        })
        startSendingScreenShareData(screenShareClonedStreamRef.current, id)
      }
    }
  }, [
    endMediaProducer,
    sendCandidateEvent,
    intialiseRecordingData,
    startSendingScreenShareData
  ])

  const createProduceTransport = useCallback(() => {
    const isInterviewOrPlacementStage =
      window?.location?.pathname?.includes('/interview-stage/') ||
      window?.location?.pathname?.includes('/ppt-stage/')

    socket?.current?.emit(
      'createWebRtcTransport',
      {
        consumer: false,
        generateRtpDump:
          !isInterviewOrPlacementStage &&
          currentStageAttempIds?.current?.attemptId
            ? true
            : false
      },
      async ({ params }) => {
        if (params.error) {
          return
        }

        const transport = device.current.createSendTransport({
          ...params,
          iceServers: ICE_SERVERS
        })

        producersTransport.current = transport
        setProducersTransportState(transport)

        producersTransport.current.on(
          'connect',
          async ({ dtlsParameters }, callback, errback) => {
            try {
              await socket?.current?.emit('transport-connect', {
                dtlsParameters
              })

              callback()
            } catch (error) {
              errback(error)
            }
          }
        )

        producersTransport.current.on(
          'produce',
          async (parameters, callback, errback) => {
            try {
              await socket?.current?.emit(
                'transport-produce',
                {
                  kind: parameters.kind,
                  rtpParameters: parameters.rtpParameters,
                  appData: parameters.appData
                },
                ({ id, producersExist, appData, error }) => {
                  if (error) {
                    console.log('Produce Error: ', error)
                    callback({})
                  } else {
                    callback({ id })

                    setPeerInfo((prev) => ({
                      ...prev,
                      [appData?.source === 'audio'
                        ? 'webcamAudioStreamId'
                        : appData?.source === 'webcam'
                        ? 'webcamVideoStreamId'
                        : 'screenStreamId']: id
                    }))

                    producerIds.current.push({
                      kind: parameters.appData.source,
                      producerId: id
                    })

                    setPeerInfo((prev) => ({
                      ...prev,
                      properties: {
                        ...(prev?.properties || {}),
                        producers: [
                          ...(prev?.properties?.producers || []),
                          {
                            kind: parameters.appData.source,
                            producerId: id
                          }
                        ]
                      }
                    }))
                  }
                }
              )
            } catch (error) {
              errback(error)
            }
          }
        )
      }
    )
  }, [])

  const createReceiverTransport = useCallback(async () => {
    await socket?.current?.emit(
      'createWebRtcTransport',
      { consumer: true },
      ({ params }) => {
        if (params.error) {
          return
        }

        let transport
        try {
          transport = device.current.createRecvTransport({
            ...params,
            iceServers: ICE_SERVERS
          })
        } catch (error) {
          return
        }

        transport.on(
          'connect',
          async ({ dtlsParameters }, callback, errback) => {
            try {
              await socket?.current?.emit('transport-recv-connect', {
                dtlsParameters,
                serverConsumerTransportId: params.id
              })

              callback()
            } catch (error) {
              errback(error)
            }
          }
        )

        consumerTransport.current = {
          consumerTransport: transport,
          params
        }

        if (consumerQueue?.current?.length > 0) {
          const queue = [...consumerQueue.current]
          consumerQueue.current = []

          queue?.forEach((producerId) => {
            const candidateObj =
              consumingTransport?.current?.[producerId] || null

            if (candidateObj) {
              connectRecvTransport(
                producerId,
                params.id,
                candidateObj?.hostname,
                candidateObj?.worker,
                candidateObj?.roomName
              )
            }
          })
        }
      }
    )
  }, [connectRecvTransport])

  const createDevice = useCallback(async () => {
    try {
      if (!device.current) {
        device.current = new Device()

        await device.current.load({
          routerRtpCapabilities: routerCapabilities.current
        })
      }

      createProduceTransport()
      createReceiverTransport()
      // getChatMessages()
    } catch (error) {}
  }, [createProduceTransport, createReceiverTransport])

  // Helper to create a silent audio track
  function createSilentAudioTrack() {
    screenShareAudioContext.current = new (window.AudioContext ||
      window.webkitAudioContext)()
    screenShareAudioSource.current =
      screenShareAudioContext.current.createOscillator()
    const dst = screenShareAudioSource.current.connect(
      screenShareAudioContext.current.createMediaStreamDestination()
    )
    screenShareAudioSource.current.start()
    return dst.stream.getAudioTracks()[0]
  }

  const getScreenShareSteam = useCallback(
    async (requiredFullShare = true) => {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          audio: false,
          video: {
            displaySurface: 'monitor',
            logicalSurface: true,
            cursor: true,
            frameRate: { ideal: 10, max: 15 },
            width: { max: 1280 },
            height: { max: 720 }
          }
        })

        // If no audio track, add a silent one
        if (stream.getAudioTracks().length === 0) {
          const silentTrack = createSilentAudioTrack()
          stream.addTrack(silentTrack)
        }

        stream
          ?.getVideoTracks()?.[0]
          ?.addEventListener('mute', screenShareMuted)

        stream
          ?.getVideoTracks()?.[0]
          ?.addEventListener('unmute', screenShareUnmuted)

        stream
          ?.getVideoTracks()?.[0]
          ?.addEventListener('ended', screenShareEnded)

        const settings = stream?.getVideoTracks()?.[0]?.getSettings()

        // Heuristic to detect fullscreen when displaySurface is not available (e.g. Firefox)
        const isLikelyFullScreen = () => {
          const capturedWidth = Math.round(settings?.width || 0)
          const capturedHeight = Math.round(settings?.height || 0)
          const screenWidth = Math.round(
            window.screen.width * window.devicePixelRatio
          )
          const screenHeight = Math.round(
            window.screen.height * window.devicePixelRatio
          )

          if (
            !capturedWidth ||
            !capturedHeight ||
            !screenWidth ||
            !screenHeight
          ) {
            return false
          }

          const screenAspect = screenWidth / screenHeight
          const captureAspect = capturedWidth / capturedHeight
          const aspectDelta = Math.abs(screenAspect - captureAspect)

          // Expected downscaled size if the browser scaled full-screen capture
          const scale = Math.min(
            capturedWidth / screenWidth,
            capturedHeight / screenHeight
          )
          const expectedWidth = Math.round(screenWidth * scale)
          const expectedHeight = Math.round(screenHeight * scale)

          const withinDimTolerance =
            Math.abs(capturedWidth - expectedWidth) <= 2 &&
            Math.abs(capturedHeight - expectedHeight) <= 2

          const withinDirectTolerance =
            Math.abs(capturedWidth - screenWidth) <= 2 &&
            Math.abs(capturedHeight - screenHeight) <= 2

          return (
            aspectDelta <= 0.02 && (withinDimTolerance || withinDirectTolerance)
          )
        }

        const updateScreenShareImageCaptureRef = (stream) => {
          if (stream?.getVideoTracks()?.[0]) {
            if (typeof window !== 'undefined' && 'ImageCapture' in window) {
              try {
                screenShareImageCaptureRef.current = new ImageCapture(
                  stream.getVideoTracks()[0]
                )
              } catch (e) {
                screenShareImageCaptureRef.current = null
              }
            } else {
              // Firefox and others without ImageCapture
              screenShareImageCaptureRef.current = null
            }
          }
        }

        if (!requiredFullShare) {
          localScreenShareStreamRef.current = stream
          screenShareClonedStreamRef.current = new MediaStream([
            stream?.getVideoTracks()?.[0]?.clone()
          ])
          updateScreenShareImageCaptureRef(stream)
          setLocalScreenShareStream(stream)
          return true
        } else if (settings?.displaySurface) {
          if (settings?.displaySurface === 'monitor') {
            localScreenShareStreamRef.current = stream
            screenShareClonedStreamRef.current = new MediaStream([
              stream?.getVideoTracks()?.[0]?.clone()
            ])
            updateScreenShareImageCaptureRef(stream)
            setLocalScreenShareStream(stream)
            return true
          } else {
            stream?.getTracks()?.forEach((track) => track.stop())
            throw new Error(
              'Please share the entire screen to continue with the challenge'
            )
          }
        } else if (isLikelyFullScreen()) {
          localScreenShareStreamRef.current = stream
          screenShareClonedStreamRef.current = new MediaStream([
            stream?.getVideoTracks()?.[0]?.clone()
          ])
          updateScreenShareImageCaptureRef(stream)
          setLocalScreenShareStream(stream)
          return true
        } else {
          stream?.getTracks()?.forEach((track) => track.stop())
          throw new Error(
            'Please share the entire screen to continue with the challenge'
          )
        }
      } catch (error) {
        console.log('Screen Share ISSUE : ', error?.message || '')
        return false
      }
    },
    [screenShareMuted, screenShareUnmuted, screenShareEnded]
  )

  const getAudioVideoStream = useCallback(
    async (needVideo = true, deviceId, groupId) => {
      needVideoRef.current = needVideo

      const isSuperViseOrInterviewScreen =
        window.location.pathname.includes('/preview') ||
        window.location.pathname.includes('/interview-stage') ||
        window.location.pathname.includes('/ppt-stage')

      try {
        let selectedGroupId = groupId
        let selectedDeviceId = deviceId

        if (!selectedDeviceId && !selectedGroupId) {
          const deviceDetails = getAudioDeviceId()

          const { inputs: inputDevices } = await getEnumeratedDevices()

          if (deviceDetails?.deviceId && deviceDetails?.groupId) {
            const index = inputDevices?.findIndex(
              (device) => device?.deviceId === deviceDetails?.deviceId
            )
            if (index !== -1) {
              selectedGroupId = inputDevices?.[index]?.groupId || null
              selectedDeviceId = inputDevices?.[index]?.deviceId || null
            } else {
              const defaultOption = inputDevices?.find(
                (item) => item?.deviceId === 'default'
              )

              if (defaultOption) {
                selectedGroupId = defaultOption?.groupId || null
                selectedDeviceId = defaultOption?.deviceId || null
              } else {
                selectedGroupId = inputDevices?.[0]?.groupId || null
                selectedDeviceId = inputDevices?.[0]?.deviceId || null
              }
            }
          } else {
            const defaultOption = inputDevices?.find(
              (item) => item?.deviceId === 'default'
            )

            if (defaultOption) {
              selectedGroupId = defaultOption?.groupId || null
              selectedDeviceId = defaultOption?.deviceId || null
            } else {
              selectedGroupId = inputDevices?.[0]?.groupId || null
              selectedDeviceId = inputDevices?.[0]?.deviceId || null
            }
          }

          setAudioDeviceId(selectedDeviceId, selectedGroupId)
          setSelectedAudioInputDeviceId({
            deviceId: selectedDeviceId,
            groupId: selectedGroupId
          })
          selectedAudioInputGroupIdRef.current = selectedGroupId
          selectedAudioInputDeviceIdRef.current = selectedDeviceId
        }

        const needFullQualityVideo =
          needFullQualityVideoPath?.some((item) =>
            reversedUrlRef?.current?.includes(item)
          ) || false

        let stream
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              deviceId: selectedDeviceId
                ? { exact: selectedDeviceId }
                : undefined,
              autoGainControl: true,
              channelCount: 1,
              echoCancellation: true,
              noiseSuppression: true
            },
            video: needVideo
              ? needFullQualityVideo
                ? true
                : {
                    width: { max: 640 },
                    height: { max: 360 },
                    frameRate: { max: 10 }
                  }
              : false
          })
        } catch (e) {
          console.log(e?.message)
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              deviceId: selectedDeviceId
                ? { exact: selectedDeviceId }
                : undefined,
              autoGainControl: true,
              channelCount: 1,
              echoCancellation: true,
              noiseSuppression: true
            },
            video: needVideo
              ? needFullQualityVideo
                ? true
                : {
                    width: { ideal: 640 },
                    height: { ideal: 480 }
                  }
              : false
          })
        }

        if (stream?.getAudioTracks()?.[0]?.muted) {
          handleProducerPaused(true, 'audio')
        } else if (!isSuperViseOrInterviewScreen) {
          handleProducerPaused(false, 'audio')
        }

        stream
          ?.getAudioTracks()?.[0]
          ?.addEventListener('mute', webcamAudioMuted)
        stream
          ?.getAudioTracks()?.[0]
          ?.addEventListener('unmute', webcamAudioUnmuted)

        stream
          ?.getAudioTracks()?.[0]
          ?.addEventListener('ended', webcamAudioEnded)

        stream
          ?.getVideoTracks()?.[0]
          ?.addEventListener('ended', webcamVideoEnded)

        localStreamRef.current = stream
        setLocalStream(stream)

        return true
      } catch (error) {
        console.log('WEBCAM AUDIO ISSUE : ', error?.message || '')
        return false
      } finally {
        setGetAudioVideoLoading(false)
      }
    },
    [
      handleProducerPaused,
      getEnumeratedDevices,
      webcamAudioMuted,
      webcamVideoEnded,
      webcamAudioUnmuted,
      webcamAudioEnded
    ]
  )

  const joinRoom = useCallback(
    (roomName, worker, mediaServerEndpoint, stageId, attemptId) => {
      const userDetails = {
        ...getUserDetails(),
        name: userProfileDetails?.name
      }

      socket?.current?.emit(
        'joinRoom',
        {
          roomName,
          worker,
          userDetails: {
            ...(userDetails || {}),
            ...(attemptId ? { attemptId } : {})
          }
        },
        async (data) => {
          if (!data?.error) {
            setPeerInfo((prev) => ({
              ...prev,
              roomName,
              ...(attemptId ? { attemptId } : {}),
              socketId: data?.socketId,
              webcamVideoStreamId: null,
              webcamAudioStreamId: null,
              screenStreamId: null,
              mediaServerEndpoint,
              worker
            }))
            setUserRoomJoined(true)
            routerCapabilities.current = data.rtpCapabilities
            createDevice()
          } else {
            resetStates(true)
            await closeMeetingSocketFully()
            excludeEndpointsRef.current = [
              ...excludeEndpointsRef.current,
              mediaServerEndpoint
            ]
            setRecallGetEndpoint({
              stageId,
              attemptId,
              call: true,
              excludeEndpoints: [...excludeEndpointsRef.current]
            })
          }
        }
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createDevice, userProfileDetails?.name, closeMeetingSocketFully]
  )

  const resetStates = useCallback((isErrorReset = false) => {
    if (!isErrorReset) {
      currentlySupervising.current = []
      setMediaState({
        audio: false,
        webcam: false,
        screen: false,
        handRaised: false
      })
      setSupervisedUser([])

      if (screenShareMutedTimeoutRef.current) {
        clearTimeout(screenShareMutedTimeoutRef.current)
        screenShareMutedTimeoutRef.current = null
      }
    }

    previousMetadataRef.current = null

    device.current = null
    producerIds.current = []

    Object.values(consumingTransport?.current || {}).forEach((ct) => {
      ct?.consumer?.close()
    })

    consumerTransport?.current?.consumerTransport?.close()
    consumerTransport.current = null

    // producersTransport?.current?.close()
    producersTransport.current = null

    videoProducer?.current?.close()
    audioProducer?.current?.close()
    screenShareVideoProducer?.current?.close()
    screenShareAudioProducer?.current?.close()
    screenShareAudioContext?.current?.close()
    screenShareAudioSource?.current?.stop()

    videoProducer.current = null
    audioProducer.current = null
    screenShareAudioSource.current = null
    screenShareAudioContext.current = null
    screenShareVideoProducer.current = null
    screenShareAudioProducer.current = null

    consumerQueue.current = []
    consumingTransport.current = {}
    routerCapabilities.current = null

    if (screenMediaRecorderRef.current) {
      screenMediaRecorderRef.current?.stop()
      screenMediaRecorderRef.current = null
    }

    if (webcamMediaRecordRef.current) {
      webcamMediaRecordRef.current?.stop()
      webcamMediaRecordRef.current = null
    }

    setChats({})
    setNewStream({})
    setUpdatedChats({})
    setConnectedUserList([])
    setUserRoomJoined(false)
    setProducersTransportState(null)
    setPeerInfo({})
  }, [])

  const initSocket = useCallback(
    async (mediaSocketEndPoint, stageId, attemptId) => {
      // If another init is already running, skip
      if (meetingInitInFlightRef.current || meetingClosingRef.current) return
      meetingInitInFlightRef.current = true

      // Compute endpoint early and validate
      const endpoint =
        MEDIA_SERVER_URL_MAP?.[mediaSocketEndPoint?.mediaServerEndpoint] ||
        mediaSocketEndPoint?.mediaServerEndpoint
      if (!endpoint) {
        meetingInitInFlightRef.current = false
        return
      }

      // Close any existing socket and wait until it is really closed
      if (socket.current) {
        await closeMeetingSocketFully()
      }

      // Reset counters only now, after the old socket is fully closed
      currentStageAttempIds.current = { stageId, attemptId }

      // Bump generation to invalidate any stale handlers
      const myGen = ++meetingGenRef.current

      // Create a brand new socket that cannot reuse old transport
      const s = io(endpoint, {
        transports: ['websocket'],
        forceNew: true,
        reconnection: false
      })
      s.__gen = myGen
      socket.current = s

      // Handlers
      s.on('end-interview-room-response', ({ roomName }) => {
        if (s.__gen !== meetingGenRef.current) return
        setEndInterviewRoom(roomName)
      })

      s.on('connection-success', () => {
        if (s.__gen !== meetingGenRef.current) return
        setIsMediaServerConnected(true)
        joinRoom(
          mediaSocketEndPoint?.roomName,
          mediaSocketEndPoint?.worker,
          mediaSocketEndPoint?.mediaServerEndpoint,
          stageId,
          attemptId
        )
        // allow new init attempts now
        meetingInitInFlightRef.current = false
      })

      s.on('connect_error', async (err) => {
        if (s.__gen !== meetingGenRef.current) return
        setIsMediaServerConnected(false)

        resetStates(true)
        await closeMeetingSocketFully()

        // Exclude this endpoint and trigger endpoint refresh
        excludeEndpointsRef.current = [
          ...excludeEndpointsRef.current,
          mediaSocketEndPoint?.mediaServerEndpoint
        ]
        setRecallGetEndpoint({
          call: true,
          stageId,
          attemptId,
          excludeEndpoints: [...excludeEndpointsRef.current]
        })
        meetingInitInFlightRef.current = false
      })

      const onDisconnected = async (reason) => {
        if (s.__gen !== meetingGenRef.current) return
        resetStates(true)
        setIsMediaServerConnected(false)
        // Free the lock so a future init can start
        meetingInitInFlightRef.current = false
      }

      s.on('disconnect', onDisconnected)

      s.on('producer-closed', ({ remoteProducerId }) => {
        if (s.__gen !== meetingGenRef.current) return
        const producerToClose = consumingTransport?.current?.[remoteProducerId]
        producerToClose?.consumer?.close()
        delete consumingTransport?.current?.[remoteProducerId]
        setPeerInfo((prev) => ({
          ...prev,
          properties: {
            ...(prev?.properties || {}),
            consumers:
              prev?.properties?.consumers?.filter(
                (consumer) => consumer?.producerId !== remoteProducerId
              ) || []
          }
        }))
        setNewStream((prev) => {
          const copy = { ...prev }
          delete copy?.[remoteProducerId]
          return copy
        })
      })

      s.on('refresh-candidate', async () => {
        if (s.__gen !== meetingGenRef.current) return
        resetStates(true)
        await closeMeetingSocketFully()
        setTimeout(() => {
          setRecallGetEndpoint({
            call: true,
            ...currentStageAttempIds.current,
            excludeEndpoints: [...excludeEndpointsRef.current]
          })
        }, 300)
        meetingInitInFlightRef.current = false
      })

      s.on('mute-user-response', () => {
        if (s.__gen !== meetingGenRef.current) return
        setMediaState((prev) => ({ ...prev, audio: true }))
      })

      s.on('warn-candidate-response', async (data) => {
        if (s.__gen !== meetingGenRef.current) return
        const message = data?.warningText || ''
        if (message) {
          await swalAlert({
            message,
            icon: 'warning',
            title: 'Warning',
            showCancelButton: false,
            allowOutsideClick: false
          })
        }
      })

      s.on('consumed', async ({ params }) => {
        if (s.__gen !== meetingGenRef.current) return
        if (!params || params?.error) return
        if (!consumingTransport?.current?.[params?.producerId]) return

        const consumer =
          await consumerTransport?.current?.consumerTransport?.consume({
            id: params.id,
            producerId: params.producerId,
            kind: params.kind,
            rtpParameters: params.rtpParameters
          })

        consumingTransport.current[params?.producerId] = {
          ...(consumingTransport?.current[params?.producerId] || {}),
          consumer
        }

        const { track } = consumer
        setNewStream((prev) => {
          const next = { ...prev }
          const currentData = consumingTransport?.current?.[params?.producerId]
          const closeIdx = Object.values(next || {}).findIndex(
            (stream) =>
              stream?.email === currentData?.email &&
              stream?.type === currentData?.type
          )
          if (closeIdx !== -1) {
            const key = Object.keys(next || {})?.[closeIdx]
            closeConsumer(key)
          }
          if (currentData) {
            next[currentData?.producerId] = {
              socketId: currentData?.socketId,
              email: currentData?.email,
              name: currentData?.name,
              role: currentData?.role,
              authRole: currentData?.authRole,
              track: new MediaStream([track]),
              paused: false,
              type: currentData?.type,
              producerId: currentData?.producerId,
              hostname: currentData?.hostname,
              worker: currentData?.worker,
              roomName: currentData?.roomName
            }
          }
          return next
        })
      })
    },
    [joinRoom, resetStates, closeConsumer, closeMeetingSocketFully]
  )

  const closeMediaTracks = useCallback(() => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop())
      localStreamRef.current = null
      setLocalStream(null)
    }

    if (localScreenShareStreamRef.current) {
      localScreenShareStreamRef.current
        .getTracks()
        .forEach((track) => track.stop())
      localScreenShareStreamRef.current = null
      screenShareImageCaptureRef.current = null
      setLocalScreenShareStream(null)
    }

    if (screenShareClonedStreamRef.current) {
      screenShareClonedStreamRef.current
        .getTracks()
        .forEach((track) => track.stop())
      screenShareClonedStreamRef.current = null
    }
  }, [])

  const closeSocket = useCallback(
    async (reconnect = false) => {
      if (!reconnect) {
        closeMediaTracks()
      }
      resetStates(reconnect)
      setIsMediaServerConnected(false)
      await closeMeetingSocketFully()
    },
    [resetStates, closeMediaTracks, closeMeetingSocketFully]
  )

  const updateDeviceId = useCallback(
    (deviceId, groupId) => {
      if (deviceId !== selectedAudioInputDeviceIdRef.current) {
        setGetAudioVideoLoading(true)
        setAudioDeviceId(deviceId, groupId)
        selectedAudioInputGroupIdRef.current = groupId
        selectedAudioInputDeviceIdRef.current = deviceId
        setSelectedAudioInputDeviceId({ deviceId, groupId })
        endMediaProducer('audio')
        getAudioVideoStream(needVideoRef.current, deviceId, groupId, true)
      }
    },
    [endMediaProducer, getAudioVideoStream]
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDeviceChange = useCallback(
    debounce(async () => {
      try {
        const { inputs, outputs } = await getEnumeratedDevices()

        // Compare the new device list with the last known state
        const newDeviceIds = inputs
          .map((device) => `${device.deviceId}-${device?.groupId}`)
          .sort()
          .join(',')
        const oldDeviceIds = devicesStateRef.current?.inputs
          ?.map((device) => `${device.deviceId}-${device?.groupId}`)
          .sort()
          .join(',')

        if (newDeviceIds === oldDeviceIds) {
          return
        }
        devicesStateRef.current = { inputs, outputs }
        const deviceDetails = getAudioDeviceId()
        const isDeviceAvailable = inputs?.find(
          (item) =>
            item?.deviceId === deviceDetails?.deviceId &&
            item?.groupId === deviceDetails?.groupId
        )

        if (!isDeviceAvailable) {
          endMediaProducer('webcam')
        }
      } catch (error) {
        console.log(error)
      }
    }, 300),
    []
  )

  // useEffects

  useEffect(() => {
    // Event listeners for online and offline events
    const handleOnline = () => {
      setIsOnline(true)
    }

    const handleOffline = () => {
      setIsOnline(false)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Cleanup event listeners when component unmounts
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      closeSocket()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    reversedUrlRef.current = location.pathname
  }, [location.pathname])

  useEffect(() => {
    const outputDeviceId = getAudioOutputDeviceIdFilter(audioOutputDevices)

    dispatch(setAudioOutputDeviceId(outputDeviceId))
  }, [audioOutputDevices, dispatch])

  useEffect(() => {
    if (isSocketConnected && socketAuthenticated && isMediaServerConnected) {
      setPeerInfo((prev) => ({
        ...prev,
        properties: {
          ...(prev?.properties || {}),
          producersState: { ...mediaState }
        }
      }))
    }
  }, [
    mediaState,
    isSocketConnected,
    socketAuthenticated,
    isMediaServerConnected
  ])

  useEffect(() => {
    if (userRoomJoined) {
      navigator?.mediaDevices?.addEventListener(
        'devicechange',
        handleDeviceChange
      )
    }
    return () => {
      navigator?.mediaDevices?.removeEventListener(
        'devicechange',
        handleDeviceChange
      )
    }
  }, [handleDeviceChange, userRoomJoined])

  useEffect(() => {
    if (producersTransportState && userRoomJoined && localStream) {
      handleAudioVideoProducer()
    }
  }, [
    producersTransportState,
    userRoomJoined,
    localStream,
    handleAudioVideoProducer
  ])

  useEffect(() => {
    if (producersTransportState && userRoomJoined && localScreenShareStream) {
      handleScreenShareProducer()
    }
  }, [
    userRoomJoined,
    producersTransportState,
    localScreenShareStream,
    handleScreenShareProducer
  ])

  useEffect(() => {
    if (
      isOnline &&
      isSocketConnected &&
      socketAuthenticated &&
      peerInfo?.worker &&
      peerInfo?.roomName &&
      peerInfo?.mediaServerEndpoint
    ) {
      sendMessage('media-server/update-peer-info', peerInfo)
    }
  }, [isOnline, isSocketConnected, socketAuthenticated, peerInfo, sendMessage])

  useEffect(() => {
    if (isSocketConnected && socketAuthenticated && isOnline) {
      editorSocket.addEventListener(
        'message',
        handleMeetingEditorSocketMessages
      )
    }
    return () => {
      if (editorSocket) {
        editorSocket.removeEventListener(
          'message',
          handleMeetingEditorSocketMessages
        )
      }
    }
  }, [
    isOnline,
    isSocketConnected,
    socketAuthenticated,
    handleMeetingEditorSocketMessages,
    editorSocket
  ])

  useEffect(() => {
    if (
      isOnline &&
      userRoomJoined &&
      isSocketConnected &&
      socketAuthenticated
    ) {
      getAllChatMessages()
    }
  }, [
    isOnline,
    isSocketConnected,
    socketAuthenticated,
    userRoomJoined,
    getAllChatMessages
  ])

  useEffect(() => {
    if (
      isOnline &&
      isSocketConnected &&
      socketAuthenticated &&
      recallGetEndPoint.call
    ) {
      setRecallGetEndpoint((prev) => ({ ...prev, call: false }))
      getMediaServerEndPoint(
        recallGetEndPoint.attemptId,
        recallGetEndPoint.stageId,
        recallGetEndPoint.excludeEndpoints
      )
    }
  }, [
    isOnline,
    isSocketConnected,
    recallGetEndPoint,
    socketAuthenticated,
    getMediaServerEndPoint
  ])

  return (
    <MeetingContext.Provider
      value={{
        newStream,
        socket: socket.current,
        localStreamRef: localStreamRef.current,
        localScreenShareStreamRef: localScreenShareStreamRef.current,
        chats,
        isOnline,
        mediaState,
        localStream,
        updatedChats,
        closeConsumer,
        supervisedUser,
        userRoomJoined,
        endInterviewRoom,
        audioInputDevices,
        connectedUserList,
        audioOutputDevices,
        screenShareImageCaptureRef,
        localScreenShareStream,
        selectedAudioInputDeviceId,
        initSocket,
        closeSocket,
        handleMuteUser,
        updateDeviceId,
        sendChatMessage,
        endMediaProducer,
        closeMediaTracks,
        setSupervisedUser,
        handleUpdateChats,
        handleChatRoomName,
        setEndInterviewRoom,
        getScreenShareSteam,
        getAudioVideoStream,
        getEnumeratedDevices,
        onRefreshStreamClick,
        handleProducerPaused,
        isMediaServerConnected,
        handleEndInterviewForAll,
        signalNewConsumerTransport,
        handleSuperviseCandidateSocket,
        handleUpdatePeerInfoProperties,
        handleCandidateWarning
      }}
    >
      <ErrorBoundary>{children}</ErrorBoundary>
    </MeetingContext.Provider>
  )
}

export default MeetingProvider
